import React, { useMemo, Fragment, useEffect, useState, useRef } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { MaterialReactTable } from 'material-react-table';
import { useGetLoggedInUserDetails } from "../../../../../hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import FormHelperText from '@mui/material/FormHelperText';
import sortBy from "lodash/sortBy";
import { useOutletContext } from 'react-router-dom';

const ManagePDFilesForRootDialog = ({ open, setOpen, PDListFromAPI, rootFolderObject, setRootFolderObject }) => {

    const { loggedInUser } = useGetLoggedInUserDetails();
    const { emailAddress } = loggedInUser;

    // console.log("PDList:", PDListFromAPI)
    const tableInstanceRef = useRef(null);

    /** */
    const handleClose = (event, reason) => {
        if (reason && reason == "backdropClick") {
            return '';
        } else {
            setOpen(false);
        }
    }

    const resourcesCMSData = useOutletContext();

    const fetchClientAccResDtlsLabel = (key) => {
        if (resourcesCMSData && resourcesCMSData?.pageContent["ClientAccountResourcesDetails"]) {
          const resourcesCMSDataLabel = resourcesCMSData.pageContent["ClientAccountResourcesDetails"];
          return returnLabel(resourcesCMSDataLabel?.Labels, key)
        }
    }

    const fetchCommonCMSLabel = (key) => {
        if (resourcesCMSData && resourcesCMSData?.pageContent["CommonComponent"]) {
            const commonCMSDataLabel = resourcesCMSData.pageContent["CommonComponent"];
            return returnLabel(commonCMSDataLabel?.Labels, key)
        }
        return '';
    }

    const returnLabel = (labelArray, key) => {
        if (labelArray.length > 0 && key) {
            return labelArray.find((x) => x.Key === key)?.Phrase || '';
        }
        return '';
    }

    const columns = useMemo(
        () => [
            {
                accessorKey: "ClientAccountProgramDescripitorTitle",
                id: 'ProgramDescripitorTitle',
                header: fetchClientAccResDtlsLabel ("ClientAccountResourcesDetails.ClientResourceTitle"), //"Client Resource Title",
                filterFn: 'contains',
                hide: true,
                muiTableHeadCellFilterTextFieldProps: 
                { placeholder: fetchClientAccResDtlsLabel ("ClientAccountResourcesDetails.PlaceholderClientResourceTitle") }
            },
            {
                accessorKey: "ProgramDescripitorFileName",
                header: fetchClientAccResDtlsLabel ("ClientAccountResourcesDetails.ResourceFileName"), //"Resource File Name",
                filterFn: 'contains',
                muiTableHeadCellFilterTextFieldProps: 
                { placeholder: fetchClientAccResDtlsLabel ("ClientAccountResourcesDetails.PlaceholderResourceFileName") }
            },
            {
                accessorKey: "ProgramDescripitorType",
                header: fetchClientAccResDtlsLabel ("ClientAccountResourcesDetails.ResourceType"), //"Resource Type",
                filterFn: 'contains',
                muiTableHeadCellFilterTextFieldProps: 
                { placeholder: fetchClientAccResDtlsLabel ("ClientAccountResourcesDetails.PlaceholderResourceType") }
            },
        ],
        []
    );

    //optionally, you can manage the row selection state yourself
    const [rowSelection, setRowSelection] = useState({});

    const rootFileList = rootFolderObject?.ProgramDescriptors || []

    /**setting check box for already selected items */
    /**for each parent folder takes what it has and show */
    useEffect(() => {
        if (!!rootFileList && !!rootFileList?.length) {
            /**{1:true,23:true} to maintain state for selected item*/
            let obj = {}
            rootFileList.forEach((item) => {
                return obj[`${item.ProgramDescripitorId}`] = true;
            })
            setRowSelection(obj)
        } else {
            setRowSelection({})
        }
    }, [rootFolderObject])

    /**collect file ids from selected folder to disable their selection in same folder.
     * 
     * we are olny checking this for selected parent only. Not for other parent folders. As we allow other parent folders to add those files without any condition.
    */
    let rootfolderPDIIdList = []
    !!rootFileList && rootFileList.forEach((eachFile) => {
        const pdFileId = eachFile?.ProgramDescripitorId
        rootfolderPDIIdList.push(pdFileId)
    })

    /**file count validation. max 20 file are allowed to create at each folder and sub folder level. */
    const [blockFileSelection, setBlockFileSelection] = useState(false)
    const [folderFileCountLimitMsg, setFolderFileCountLimitMsg] = useState("")
    useEffect(() => {
        if (rootfolderPDIIdList.length === 20 || rootfolderPDIIdList.length > 20) {
            //setFolderFileCountLimitMsg("Max 20 resource file could be added at this level.  No more files can be added.")
            setFolderFileCountLimitMsg(fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.ErrorMsgMaxResourceFile'))
            setBlockFileSelection(true)
        } else {
            setFolderFileCountLimitMsg("")
            setBlockFileSelection(false)
        }
    }, [rootfolderPDIIdList])

    const handleAddList = () => {
        /**get list of selected objects */
        const selectedRowObjList = tableInstanceRef.current
            .getSelectedRowModel()
            .rows.map((eachSelectedRowModel) => {

                return {
                    ProgramDescripitorId: eachSelectedRowModel?.original?.ProgramDescripitorId,
                    ClientAccountProgramDescripitorTitle: eachSelectedRowModel?.original?.ClientAccountProgramDescripitorTitle,
                    IsActive: eachSelectedRowModel?.original?.IsActive,
                    CreatedBy: emailAddress || ""
                }
            });

        // console.log("selectedItem:", selectedRowObjList)

        /**sort file array then set to  setRootFolderObject*/
        const inputArrayForSorting = selectedRowObjList || []

        const sortedFileArray = sortBy(inputArrayForSorting, function (item) {
            const clientAccountProgramDescripitorTitle = item?.ClientAccountProgramDescripitorTitle || ""
            /**we are converting all file names into smallcase for proper aphabetical order */
            return [clientAccountProgramDescripitorTitle.toLowerCase()];
        });

        // console.log("sortedFileArray:", sortedFileArray)

        const modifiedRootFolderObj = {
            ...rootFolderObject,
            ProgramDescriptors: sortedFileArray || []
        }

        setRootFolderObject(modifiedRootFolderObj)

        handleClose()
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            disableEscapeKeyDown
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
                {/* Select Resource Files */}
                {fetchClientAccResDtlsLabel('ClientAccountResourcesDetails.SelectResourceFiles')}

                <IconButton
                    sx={{ ml: 'auto' }}
                    onClick={handleClose}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {
                    !!blockFileSelection && (
                        <FormHelperText error={!!blockFileSelection}>{folderFileCountLimitMsg}</FormHelperText>
                    )
                }
                <MaterialReactTable
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    data={PDListFromAPI || []}
                    // layoutMode="grid"
                    enableGlobalFilter={false}
                    enableDensityToggle={false}
                    initialState={{
                        density: 'compact',
                        showColumnFilters: true
                    }}
                    /**conditionally enable rows with checkbox, rest diable rows */
                    enableRowSelection={(row) => {
                        if (!!blockFileSelection) {
                            return false
                        } else {
                            return !rootfolderPDIIdList.includes(row.original.ProgramDescripitorId)
                        }
                    }}

                    getRowId={(originalRow) => originalRow.ProgramDescripitorId}
                    onRowSelectionChange={setRowSelection} //connect internal row selection state to your own
                    state={{ rowSelection }}
                    enableStickyHeader
                    muiTablePaginationProps={{
                        labelRowsPerPage: fetchCommonCMSLabel('Common.Rowsperpage') //Rows per page
                      }}
                />

            </DialogContent>
            <DialogActions>
                <Button
                    type='button'
                    onClick={() => {
                        handleAddList()
                    }}
                >{fetchCommonCMSLabel("Common.Add")}</Button>
                <Button onClick={handleClose}>{fetchCommonCMSLabel("Common.Cancel")}</Button>
            </DialogActions>

        </Dialog>
    )
}
export default ManagePDFilesForRootDialog